import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Home.css';

const Home = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <h1>Welcome to DealerHub</h1>
          <p className="hero-subtitle">The modern car auction platform for dealers</p>
          <p className="hero-description">
            Buy and sell vehicles in real-time. Fast, transparent, and secure auctions
            with instant settlement.
          </p>
          <div className="hero-cta">
            {isAuthenticated ? (
              <Link to="/dashboard" className="btn btn-primary btn-lg">
                Go to Dashboard
              </Link>
            ) : (
              <>
                <Link to="/signup" className="btn btn-primary btn-lg">
                  Get Started as a Seller
                </Link>
                <Link to="/login" className="btn btn-secondary btn-lg">
                  Sign In
                </Link>
              </>
            )}
          </div>
        </div>
        <div className="hero-image">
          <div className="hero-placeholder">
            <svg viewBox="0 0 400 300" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="400" height="300" fill="#f0f4f8"/>
              <circle cx="200" cy="100" r="40" fill="#0066cc"/>
              <rect x="100" y="150" width="200" height="100" fill="#e0e8f0" rx="8"/>
              <line x1="120" y1="180" x2="280" y2="180" stroke="#0066cc" strokeWidth="2"/>
              <line x1="120" y1="210" x2="250" y2="210" stroke="#0066cc" strokeWidth="2"/>
            </svg>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <h2>Why Choose DealerHub?</h2>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">⚡</div>
            <h3>Real-Time Bidding</h3>
            <p>Live auction updates with WebSocket technology. Place bids instantly and see competition in real-time.</p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">💰</div>
            <h3>Transparent Pricing</h3>
            <p>5% commission rate with no hidden fees. Clear reserve prices and market data for every vehicle.</p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">⏱️</div>
            <h3>Fast Settlement</h3>
            <p>ACH-based payments settle in 24-48 hours. No waiting for checks or wire transfers.</p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">🔒</div>
            <h3>Secure & Compliant</h3>
            <p>Bank-grade encryption, fraud detection, and full audit trails for every transaction.</p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">📊</div>
            <h3>Advanced Analytics</h3>
            <p>Track your sales, revenue, and performance metrics in real-time dashboard.</p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">🚀</div>
            <h3>Mobile Ready</h3>
            <p>Fully responsive design. Manage auctions from desktop, tablet, or phone.</p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats">
        <div className="stat-item">
          <h3 className="stat-number">67+</h3>
          <p className="stat-label">API Endpoints</p>
        </div>
        <div className="stat-item">
          <h3 className="stat-number">100%</h3>
          <p className="stat-label">Uptime SLA</p>
        </div>
        <div className="stat-item">
          <h3 className="stat-number">24-48h</h3>
          <p className="stat-label">Settlement</p>
        </div>
        <div className="stat-item">
          <h3 className="stat-number">5%</h3>
          <p className="stat-label">Commission</p>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works">
        <h2>How It Works</h2>
        <div className="steps">
          <div className="step">
            <div className="step-number">1</div>
            <h3>Create Account</h3>
            <p>Sign up as a seller or buyer in seconds. Verify your email and set up your profile.</p>
          </div>

          <div className="step">
            <div className="step-number">2</div>
            <h3>List Vehicles</h3>
            <p>Add vehicles with photos and details. Set starting price and reserve price.</p>
          </div>

          <div className="step">
            <div className="step-number">3</div>
            <h3>Run Auctions</h3>
            <p>Launch auctions and watch real-time bids. Automatic bid increments and extensions.</p>
          </div>

          <div className="step">
            <div className="step-number">4</div>
            <h3>Settle & Pay</h3>
            <p>Winning bids automatically settle. ACH transfers within 24-48 hours.</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <h2>Ready to Start Selling?</h2>
        <p>Join hundreds of dealers already using DealerHub</p>
        {!isAuthenticated && (
          <Link to="/signup" className="btn btn-primary btn-lg">
            Create Your Account Now
          </Link>
        )}
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>DealerHub</h4>
            <p>The modern car auction platform</p>
          </div>
          <div className="footer-section">
            <h4>Product</h4>
            <ul>
              <li><a href="#features">Features</a></li>
              <li><a href="#pricing">Pricing</a></li>
              <li><a href="#api">API Docs</a></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>Company</h4>
            <ul>
              <li><a href="#about">About</a></li>
              <li><a href="#contact">Contact</a></li>
              <li><a href="#terms">Terms of Service</a></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>Legal</h4>
            <ul>
              <li><a href="#privacy">Privacy Policy</a></li>
              <li><a href="#security">Security</a></li>
              <li><a href="#compliance">Compliance</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2026 DealerHub. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
